import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import useFeatureFlags from 'api/useFeatureFlags';
import useImages from 'api/useImages';
import useOffers from 'api/useOffers';
import { useProduct } from 'api/useProduct';
import {
  Offer,
  PopUpProductSequence,
  PopUpSequenceProduct
} from 'common/contracts';
import {
  EFeatureFlag,
  EPopupSubType,
  ESaleDiscountDisplayType,
  OfferType
} from 'constants/enums';
import { v4 as uuidv4 } from 'uuid';
import { OfferFormLayout } from 'views/Offers/components/OfferFormLayout';
import { useOfferDesignOptions } from 'views/Offers/hooks/useOfferDesignOptions';
import { useOfferFormSubmit } from 'views/Offers/hooks/useOfferFormSubmit';
import { DEFAULT_OFFER_DESIGN_EXTERNAL_ID } from 'views/Offers/Offer.consts';
import { checkUniqueness } from 'views/Offers/Offer.utils';
import PreviewSection from 'views/Offers/Popups/Form/components/Sections/PreviewSection';
import { usePopupProducts } from 'views/Offers/Popups/Form/hooks/usePopupProducts';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import { Form } from 'components/ui';

import { ProductEntry } from '../../Popups/Form/hooks/usePopupProducts';

import { RewardCalendarContent } from './components/RewardCalendarContent';
import {
  REWARD_CALENDAR_DEFAULT_VALUES,
  REWARD_CALENDAR_SCHEMA,
  RewardCalendarFormFields
} from './RewardCalendarForm.consts';

interface IRewardCalendarFormProps {
  edit?: boolean;
  dup?: boolean;
}

const RewardCalendarForm: FC<IRewardCalendarFormProps> = ({
  edit = false,
  dup = false
}) => {
  const { publisherId } = useParams();
  const { featureFlags, isLoading } = useFeatureFlags({ publisherId });

  if (isLoading) {
    return null;
  }

  if (!featureFlags?.[EFeatureFlag.DASHBOARD_REWARD_CALENDAR]) {
    return <Navigate to="../popups" replace />;
  }

  return <RewardCalendarFormContent edit={edit} dup={dup} />;
};

const RewardCalendarFormContent: FC<IRewardCalendarFormProps> = ({
  edit = false,
  dup = false
}) => {
  const { publisherId, popupId } = useParams();
  const navigate = useNavigate();
  const { getProducts } = useProduct(publisherId);
  const {
    addPopUpOffer,
    updatePopUpOffer,
    getOffer,
    getOffers,
    formatProductQuantity
  } = useOffers({ offerId: popupId, offerType: OfferType.POPUP });

  const { submitOffer } = useOfferFormSubmit({
    edit,
    dup,
    offerLabel: 'Reward Calendar',
    addOffer: addPopUpOffer,
    updateOffer: updatePopUpOffer,
    onSuccess: (): void => {
      getOffers.refetch();
    }
  });

  const { getImages } = useImages(publisherId);
  const { offerDesignOptions, defaultOfferDesignId: defaultOfferUiId } =
    useOfferDesignOptions({
      publisherId: publisherId || '',
      offerDesignType: OfferDesignTypes.PopUp,
      defaultExternalId: DEFAULT_OFFER_DESIGN_EXTERNAL_ID[OfferType.POPUP]
    });

  const [activeTab, setActiveTab] = useState('settings');

  const offers = useMemo(
    () => getOffers.data?.offers || [],
    [getOffers.data?.offers]
  );
  const currentOffer = getOffer.data?.result;

  const form = useForm<RewardCalendarFormFields>({
    resolver: zodResolver(REWARD_CALENDAR_SCHEMA),
    mode: 'onChange',
    defaultValues: REWARD_CALENDAR_DEFAULT_VALUES
  });

  const {
    productsList,
    addProduct,
    deleteProduct,
    updateProduct,
    setMultiProducts: setProducts
  } = usePopupProducts(false);

  const isNameTaken = !checkUniqueness(
    form.watch('name'),
    'name',
    offers,
    currentOffer,
    edit
  );
  const isPublisherOfferIdTaken = !checkUniqueness(
    form.watch('publisherOfferId'),
    'publisherOfferId',
    offers,
    currentOffer,
    edit
  );

  useEffect(() => {
    form.register('products');
  }, [form, form.register]);

  const currentOfferUI = useWatch({
    control: form.control,
    name: 'offerUiId',
    defaultValue: defaultOfferUiId
  });

  useEffect(() => {
    if (!popupId && defaultOfferUiId) {
      form.setValue('offerUiId', defaultOfferUiId, {
        shouldDirty: false,
        shouldValidate: true
      });
    }

    if ((edit || dup) && currentOffer) {
      const products: ProductEntry[] = (
        currentOffer.productsSequence as PopUpProductSequence[]
      )
        .filter((seq) => seq.products.length > 0)
        .map((seq) => {
          const product = seq.products[0] as PopUpSequenceProduct;
          return {
            id: uuidv4(),
            productId: product.product?.productId ?? '',
            quantity: product.quantity ?? 0,
            displayName: product.product?.displayName ?? '',
            image: product.product?.images?.[0]?.url ?? '',
            quantityDisplay: product?.quantityDisplay
          };
        });

      setProducts(products);

      const existing = currentOffer as Partial<Offer> & {
        startTimeZone?: RewardCalendarFormFields['startTimeZone'];
        startTime?: string | Date;
      };
      const existingStartTime = existing.startTime
        ? new Date(existing.startTime)
        : REWARD_CALENDAR_DEFAULT_VALUES.startTime;

      form.reset({
        ...REWARD_CALENDAR_DEFAULT_VALUES,
        ...currentOffer,
        active: dup || edit ? currentOffer.active : true,
        name: dup ? `${currentOffer.name}_copy` : currentOffer.name,
        publisherOfferId: dup
          ? `${currentOffer.publisherOfferId}_copy`
          : currentOffer.publisherOfferId,
        offerUiId:
          currentOffer.offerUi?._id ||
          currentOffer.offerUi?.offerUiId ||
          defaultOfferUiId,
        priority:
          currentOffer.priority ?? REWARD_CALENDAR_DEFAULT_VALUES.priority,
        subType: EPopupSubType.DAILY_BONUS,
        startOver: currentOffer.hasOwnProperty('startOver')
          ? currentOffer.startOver
          : true,
        startTimeZone:
          existing.startTimeZone ??
          REWARD_CALENDAR_DEFAULT_VALUES.startTimeZone,
        startTime: existingStartTime,
        products
      } as RewardCalendarFormFields);
    }
  }, [edit, dup, currentOffer, form, popupId, defaultOfferUiId]);

  const onSubmit = useCallback(
    (data: RewardCalendarFormFields): void => {
      const productsSequence = productsList.map((product, index) => ({
        index: index + 1,
        priceInUsdCents: 0,
        products: [
          {
            productId: product.productId,
            quantity: product.quantity
          }
        ]
      }));

      const payload: Partial<Offer> & {
        startTimeZone: RewardCalendarFormFields['startTimeZone'];
        startTime: Date;
      } = {
        active: data.active,
        coolDownInHours: 1,
        displayName: data.name,
        name: data.name,
        offerUiId: data.offerUiId,
        priceDiscount: {
          discount: 0,
          type: ESaleDiscountDisplayType.PERCENTAGE
        },
        priority: data.priority,
        productsSequence:
          productsSequence.length > 0
            ? productsSequence
            : [{ index: 1, priceInUsdCents: 0, products: [] }],
        publisherOfferId: data.publisherOfferId,
        segments: data.segments,
        showAfter: 'login',
        startOver: data.startOver,
        startTimeZone: data.startTimeZone,
        startTime: data.startTime,
        subType: EPopupSubType.DAILY_BONUS,
        tags: [],
        triggers: [{ type: 'time', eventName: 'impression', every: 1440 }],
        type: OfferType.POPUP
      };
      submitOffer(payload, popupId);
    },
    [productsList, submitOffer, popupId]
  );

  const watchedName = form.watch('name');

  const formTitle = useMemo((): string => {
    if (edit) {
      return `Edit Reward Calendar (${watchedName || currentOffer?.name || ''})`;
    }
    if (dup) {
      return 'Duplicate Reward Calendar';
    }
    return 'Create Reward Calendar';
  }, [edit, dup, watchedName, currentOffer?.name]);

  const isFormValid = useMemo(
    (): boolean =>
      form.formState.isValid &&
      (dup || form.formState.isDirty) &&
      !isNameTaken &&
      !isPublisherOfferIdTaken,
    [
      form.formState.isValid,
      form.formState.isDirty,
      dup,
      isNameTaken,
      isPublisherOfferIdTaken
    ]
  );

  const handleSave = useCallback((): void => {
    form.handleSubmit(onSubmit)();
  }, [form, onSubmit]);

  const handleClose = useCallback((): void => {
    navigate('../');
  }, [navigate]);

  let buttonText = 'Save';
  if (edit) {
    buttonText = 'Update';
  }

  const isImagesReady = !getImages.isLoading && !!getImages.data;

  return (
    <OfferFormLayout
      title={formTitle}
      mainButtonText={buttonText}
      isFormValid={isFormValid}
      onSave={handleSave}
      onClose={handleClose}
      previewContent={
        <PreviewSection
          publisherId={publisherId}
          currentOfferUI={currentOfferUI}
          isDailyBonus
          currentPrice={0}
          products={getProducts.data}
          productsList={productsList}
        />
      }
    >
      {isImagesReady && (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-7 mb-9 w-[456px] mx-auto flex flex-col gap-y-9"
          >
            <RewardCalendarContent
              form={form}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              offerDesignOptions={offerDesignOptions}
              isNameTaken={isNameTaken}
              isPublisherOfferIdTaken={isPublisherOfferIdTaken}
              getProducts={getProducts}
              productsList={productsList}
              addProduct={addProduct}
              deleteProduct={deleteProduct}
              updateProduct={updateProduct}
              formatProductQuantity={formatProductQuantity}
            />
          </form>
        </Form>
      )}
    </OfferFormLayout>
  );
};

export { RewardCalendarForm };
